import React, { useEffect, useState } from 'react';
import api from '../../api';
import { useAuth } from '../../components/Layouts/context/Authcontext';
import PieChart from '../Dashboard/PieChart';
import { getRoundRobinBaseUrl } from '../../loadBalancer';
import { motion } from 'framer-motion';

function Rating() {
  const { accessToken } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [kpiRatingEmployeeCount, setKpiRatingEmployeeCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const fetchEmployeeRatings = async () => {
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/userDashboard/getReportingKpIRating`, {
        params: { month: selectedMonth, year: selectedYear },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data?.success && Array.isArray(response.data.findEmployeeKpiRating)) {
        setEmployees(response.data.findEmployeeKpiRating);
        setKpiRatingEmployeeCount(response.data.findKpiRatingEmployeeCount);
      } else {
        setEmployees([]);
        setError('Invalid data format from API');
      }
    } catch (error) {
      setEmployees([]);
      setError('Error fetching data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeRatings();
  }, [accessToken, selectedMonth, selectedYear]);

  const getBadgeColor = (rating) => {
    switch (rating) {
      case 'Below Expectations':
        return 'bg-red-500 text-white';
      case 'Meeting Expectations':
        return 'bg-yellow-400 text-black';
      case 'Exceeding Expectations':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  };

  const getDefinition = (rating) => {
    switch (rating) {
      case 'Below Expectations':
        return 'Performance below the expected standard.';
      case 'Meeting Expectations':
        return 'Consistent and satisfactory performance.';
      case 'Exceeding Expectations':
        return 'Outstanding contribution beyond requirements.';
      default:
        return 'No definition available.';
    }
  };

  return (
    <div className="p-8 relative bg-gradient-to-tr from-gray-100 via-white to-gray-200 min-h-screen">
      <style>
        {`
          @keyframes localGradientX {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .local-animate-gradient {
            background: linear-gradient(270deg, #1e3a8a, #dc2626, #1e3a8a);
            background-size: 600% 600%;
            animation: localGradientX 6s ease infinite;
          }
        `}
      </style>

      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">📊 current month Employee Ratings</h2>

      {/* Animated Filter */}
      <motion.div
        className="absolute top-4 right-4 local-animate-gradient shadow-xl rounded-xl px-4 py-2 flex space-x-2 backdrop-blur-md border border-white/30"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="px-2 py-1 rounded-md text-sm bg-white/30 backdrop-blur text-white hover:bg-white/40 transition duration-300"
        >
          {months.map((m) => (
            <option key={m.value} value={m.value} className="text-black">
              {m.label}
            </option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-2 py-1 rounded-md text-sm bg-white/30 backdrop-blur text-white hover:bg-white/40 transition duration-300"
        >
          {years.map((y) => (
            <option key={y} value={y} className="text-black">
              {y}
            </option>
          ))}
        </select>
      </motion.div>

      {/* Table Section */}
      <div className="panel shadow-lg rounded-lg overflow-hidden mt-8">
        {loading ? (
          <p className="text-center text-gray-600 text-lg">Loading data...</p>
        ) : error ? (
          <p className="text-center text-red-500 text-lg">{error}</p>
        ) : employees.length === 0 ? (
          <p className="text-center text-gray-600 text-lg">No employee data available.</p>
        ) : (
          <>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full border border-gray-300">
                <thead className="bg-gradient-to-r from-blue-700 to-red-600 text-white uppercase text-sm">
                  <tr>
                    <th className="py-4 px-6 text-left border">S.No</th>
                    <th className="py-4 px-6 text-left border">Location</th>
                    <th className="py-4 px-6 text-left border">Emp ID</th>
                    <th className="py-4 px-6 text-left border">Emp Name</th>
                    <th className="py-4 px-6 text-left border">Department</th>
                    <th className="py-4 px-6 text-left border">Designation</th>
                    <th className="py-4 px-6 text-center border">Final Rating</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 text-sm font-medium justify-center">
                  {employees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((employee, index) => (
                    <tr
                      key={employee.EmpId || index}
                      className={`border-b transition-all duration-200 ${
                        index % 2 === 0 ? 'bg-gray-100' : 'bg-white'
                      } hover:bg-gray-200`}
                    >
                      <td className="py-4 px-6 border text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="py-4 px-6 border">{employee.Location || 'N/A'}</td>
                      <td className="py-4 px-6 border">{employee.EmpId || 'N/A'}</td>
                      <td className="py-4 px-6 border">{employee.EmpName || 'N/A'}</td>
                      <td className="py-4 px-6 border">{employee.Department || 'N/A'}</td>
                      <td className="py-4 px-6 border">{employee.Designation || 'N/A'}</td>
                      <td className="py-4 px-6 border text-center">
                        <div className="group relative flex flex-col items-center justify-center">
                          <div
                            className={`px-4 py-2 rounded-full font-semibold text-sm shadow-md transition-transform transform group-hover:scale-105 cursor-pointer ${getBadgeColor(
                              employee.KPILegend
                            )}`}
                          >
                            {employee.KPILegend || 'N/A'}
                          </div>
                          <div className="absolute z-10 top-14 bg-white text-gray-800 border border-gray-300 rounded-lg shadow-lg p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-[180px]">
                            {getDefinition(employee.KPILegend)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4 p-4 bg-gray-100 rounded-lg">
              <button
                className={`px-4 py-2 rounded-lg font-semibold ${
                  currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="text-gray-800 font-semibold">
                Page {currentPage} of {Math.ceil(employees.length / itemsPerPage)}
              </span>
              <button
                className={`px-4 py-2 rounded-lg font-semibold ${
                  currentPage === Math.ceil(employees.length / itemsPerPage)
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === Math.ceil(employees.length / itemsPerPage)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Pie Chart */}
      <div className="flex justify-center items-center mt-12">
        <div className="w-full md:w-2/3 lg:w-1/2 bg-white panel shadow-lg rounded-lg p-6">
          <PieChart kpiRatingEmployeeCount={kpiRatingEmployeeCount} selectedMonth={selectedMonth} selectedYear={selectedYear} />
        </div>
      </div>
    </div>
  );
}

export default Rating;
